import { AlertTriangle, CheckCircle2, Clock, FileText } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router";
import { toast } from "sonner";

import { AuthLayout } from "@/components/auth/AuthLayout";
import { CamposDeFacturacion } from "@/components/facturacion/CamposDeFacturacion";
import {
  FACTURACION_VACIA,
  facturacionSchema,
  type FacturacionValues,
} from "@/components/facturacion/esquemaDeFacturacion";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Spinner } from "@/components/ui/spinner";
import {
  useAceptarConEnlace,
  useComunasParaAceptar,
  useCotizacionParaAceptar,
} from "@/hooks/useAceptacionPublica";
import { ApiError, getApiErrorMessage, parseValidationErrors } from "@/lib/api";
import { cn } from "@/lib/utils";

/**
 * Pantalla a la que llega EL CLIENTE desde el enlace del correo de presupuesto
 * (entrega D).
 *
 * SIN LOGIN, y es la única pantalla del sistema que no lo tiene además de las
 * de autenticación. El cliente no tiene cuenta en Consultek y nunca la va a
 * tener: su credencial es el token de la URL.
 *
 * Reusa `AuthLayout` a propósito y no el shell de la app: ese layout ya está
 * escrito para que lo vea alguien de afuera —su propio docblock lo dice— y no
 * monta menú, barra superior ni nada que suponga una sesión.
 *
 * NO NOMBRA NADA INTERNO. No dice "informe", no dice en qué estado queda, no
 * menciona revisores ni pantallas. Del otro lado hay un cliente que pidió una
 * tasación, no un usuario del sistema.
 *
 * Los TRES rechazos del backend tienen cada uno su pantalla, porque a un
 * cliente le dicen cosas distintas y confundirlas lo deja sin saber qué hacer:
 *
 * - **410**: el enlace venció o ya se usó. Quien aceptó ayer tiene que leer
 *   "ya está aceptada", no un error, o va a creer que su respuesta se perdió.
 * - **409**: la cotización se reemplazó por una nueva. Lo que corresponde es
 *   decirle que espere el presupuesto actualizado.
 * - **404**: el enlace no es válido — típicamente un token cortado al copiarlo
 *   desde el correo.
 */
export function AceptarCotizacion() {
  const { token } = useParams<{ token: string }>();
  const [aceptada, setAceptada] = useState(false);
  // `null` es "todavía no contestó": el botón sigue apagado. No hay respuesta
  // por defecto a propósito — una pregunta ya contestada por el sistema es una
  // factura que se pide (o se omite) sin que el cliente haya decidido nada.
  const [respuesta, setRespuesta] = useState<"si" | "no" | null>(null);

  const consulta = useCotizacionParaAceptar(token);
  const aceptar = useAceptarConEnlace(token);
  // Las comunas se piden SOLO si dice que sí: quien responde que no no necesita
  // bajarse 25 KB que no va a mirar.
  const comunas = useComunasParaAceptar(token, respuesta === "si");

  const form = useForm<FacturacionValues>({
    resolver: zodResolver(facturacionSchema),
    defaultValues: FACTURACION_VACIA,
  });

  // Enlace sin token: ni vale la pena pegarle a la API.
  if (!token) {
    return (
      <Estado
        icon={<AlertTriangle className="h-6 w-6" />}
        tono="error"
        titulo="Enlace no válido"
        descripcion="Este enlace está incompleto. Revisa que lo hayas copiado entero desde el correo, o responde ese correo y te ayudamos."
      />
    );
  }

  if (consulta.isLoading) {
    return <Cargando />;
  }

  if (consulta.isError) {
    const status = consulta.error instanceof ApiError ? consulta.error.status : null;

    if (status === 410) {
      return (
        <Estado
          icon={<Clock className="h-6 w-6" />}
          tono="neutro"
          titulo="Este enlace ya no está disponible"
          descripcion="Puede que ya hayas aceptado la cotización o que el enlace haya vencido. Si necesitas ayuda, responde el correo que te enviamos."
        />
      );
    }

    if (status === 409) {
      return (
        <Estado
          icon={<Clock className="h-6 w-6" />}
          tono="neutro"
          titulo="Esta cotización se actualizó"
          descripcion="El presupuesto que aparecía en este enlace fue reemplazado por uno nuevo. Si estás esperando el presupuesto actualizado, te llegará por correo."
        />
      );
    }

    return (
      <Estado
        icon={<AlertTriangle className="h-6 w-6" />}
        tono="error"
        titulo="Enlace no válido"
        descripcion="No pudimos abrir esta cotización. Revisa que hayas copiado el enlace entero desde el correo, o responde ese correo y te ayudamos."
      />
    );
  }

  if (!consulta.data) {
    // Estado transitorio (justo antes de que `isLoading` refleje el fetch en
    // curso): mismo criterio que `ActivarCuenta` — no reventar contra
    // `undefined` y mostrar el mismo cargando.
    return <Cargando />;
  }

  const cotizacion = consulta.data;
  // Pausada NO es en vuelo: sin red, TanStack deja la mutación `isPending` sin
  // haber enviado nada, y el botón quedaría girando para siempre.
  const enVuelo = aceptar.isPending && !aceptar.isPaused;

  const alError = (error: unknown) => {
    // El 422 del servidor se pinta SOBRE los campos —el del RUT es el único que
    // puede decir algo que el navegador no sabe— y no se acepta nada: el enlace
    // sigue vivo para corregir y volver a intentar.
    const errores = parseValidationErrors(error);
    if (errores) {
      for (const [campo, mensaje] of Object.entries(errores)) {
        if (campo in FACTURACION_VACIA) {
          form.setError(campo as keyof FacturacionValues, { message: mensaje });
        }
      }
      return;
    }

    toast.error(
      getApiErrorMessage(
        error,
        "No pudimos confirmar tu aceptación. Inténtalo de nuevo en unos minutos.",
      ),
    );
  };

  function handleAceptar() {
    if (respuesta === "no") {
      // Lo que se haya escrito antes de cambiar de opinión NO viaja.
      aceptar.mutate(
        { requiere_factura: false },
        { onSuccess: () => setAceptada(true), onError: alError },
      );
      return;
    }

    if (respuesta === "si") {
      void form.handleSubmit((valores) =>
        aceptar.mutate(
          {
            requiere_factura: true,
            ...valores,
            // Los dos opcionales viajan como `null` y no como cadena vacía,
            // igual que en el portal.
            telefono: valores.telefono || null,
            contacto_nombre: valores.contacto_nombre || null,
          },
          { onSuccess: () => setAceptada(true), onError: alError },
        ),
      )();
    }
  }

  if (aceptada) {
    return (
      <Estado
        icon={<CheckCircle2 className="h-6 w-6" />}
        tono="exito"
        titulo="¡Listo! Cotización aceptada"
        descripcion="Te enviamos un correo con los antecedentes que necesitamos para comenzar. Mientras antes los tengamos, antes empezamos el trabajo."
      />
    );
  }

  return (
    <AuthLayout>
      <div className="mb-8">
        <div className="bg-accent text-accent-foreground mb-5 flex h-12 w-12 items-center justify-center rounded-2xl">
          <FileText className="h-6 w-6" />
        </div>

        <h1 className="text-3xl font-extrabold tracking-tight">Aceptar cotización</h1>
        <p className="text-muted-foreground text-caption mt-2">
          {cotizacion.propiedad
            ? `Servicio de tasación para ${cotizacion.propiedad}.`
            : "Servicio de tasación."}{" "}
          Revisa los datos y confirma para que comencemos.
        </p>

        <dl className="bg-muted/50 mt-6 space-y-3 rounded-xl border p-5 text-sm">
          {cotizacion.cliente && (
            <div className="flex items-baseline justify-between gap-4">
              <dt className="text-muted-foreground">Cliente</dt>
              <dd className="text-right font-medium">{cotizacion.cliente}</dd>
            </div>
          )}

          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-muted-foreground">Valor del servicio</dt>
            {/* El precio ya viene con su unidad desde el API: la pantalla no
                asume la moneda ni la recalcula. */}
            <dd className="text-primary text-lg font-extrabold">{cotizacion.precio_uf}</dd>
          </div>

          {cotizacion.plazo && (
            <div className="flex items-baseline justify-between gap-4">
              <dt className="text-muted-foreground">Plazo de entrega</dt>
              <dd className="text-right font-medium">{cotizacion.plazo}</dd>
            </div>
          )}

          <div className="flex items-baseline justify-between gap-4">
            <dt className="text-muted-foreground">Validez de la oferta</dt>
            <dd className="text-right font-medium">{cotizacion.validez}</dd>
          </div>
        </dl>

        {/* Lo que pasa al confirmar, ANTES del botón, porque no hay forma de
            deshacerlo desde acá.

            NO DICE que el plazo empieza a correr, y eso es lo importante: no
            empieza. El plazo corre a contar de la visita de inspección —lo
            dice el presupuesto que el cliente tiene abierto al lado— y esa
            visita necesita justamente los antecedentes que se le piden acá
            abajo. Prometerle que el reloj arranca con este clic lo dejaba
            esperando una entrega que nadie le ofreció. */}
        <p className="text-muted-foreground text-caption mt-4">
          Al aceptar, te enviaremos por correo la lista de antecedentes que necesitamos para
          coordinar la visita a terreno.
        </p>

        {/* LA PREGUNTA VA ANTES DEL BOTÓN y es parte de aceptar, no un paso
            aparte: así no puede quedar una cotización aceptada sin la
            respuesta. Dos tarjetas grandes y no un interruptor, para que
            decidir sea un clic y no haya un valor por defecto escondido. */}
        <fieldset className="mt-6">
          <legend className="text-h3 mb-3">¿Requieres factura?</legend>
          <RadioGroup
            value={respuesta ?? ""}
            onValueChange={(valor) => setRespuesta(valor === "si" ? "si" : "no")}
            className="grid-cols-1 gap-3 sm:grid-cols-2"
          >
            <OpcionDeFactura
              valor="si"
              titulo="Sí, necesito factura"
              descripcion="Te pediremos los datos para emitirla."
              elegida={respuesta === "si"}
            />
            <OpcionDeFactura
              valor="no"
              titulo="No, no la necesito"
              descripcion="No tienes que ingresar ningún dato."
              elegida={respuesta === "no"}
            />
          </RadioGroup>
        </fieldset>

        {respuesta === "si" && (
          <Form {...form}>
            {/* `noValidate`: hay un `<input type="email">` y la validación
                nativa le ganaría a zod, dejando un campo que rechaza sin
                decir por qué. */}
            <form
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                handleAceptar();
              }}
              className="mt-6 space-y-5"
            >
              <CamposDeFacturacion form={form} comunas={comunas} />
            </form>
          </Form>
        )}

        <Button
          size="lg"
          className="mt-6 w-full"
          disabled={enVuelo || respuesta === null}
          onClick={handleAceptar}
        >
          {enVuelo && <Spinner />}
          {enVuelo ? "Confirmando…" : "Aceptar cotización"}
        </Button>

        <p className="text-muted-foreground mt-4 text-center text-xs">
          Si prefieres aceptarla por escrito o tienes dudas, responde el correo del presupuesto.
        </p>
      </div>
    </AuthLayout>
  );
}

/**
 * Una de las dos respuestas, como tarjeta entera clicable: el cliente decide
 * con un clic grande y la elegida se ve de lejos (borde y fondo del DS).
 */
function OpcionDeFactura({
  valor,
  titulo,
  descripcion,
  elegida,
}: {
  valor: "si" | "no";
  titulo: string;
  descripcion: string;
  elegida: boolean;
}) {
  const id = `requiere-factura-${valor}`;

  return (
    <label
      htmlFor={id}
      className={cn(
        "flex cursor-pointer items-start gap-3 rounded-xl border-[1.5px] p-4 transition-colors",
        elegida ? "border-primary bg-accent" : "hover:bg-muted/50",
      )}
    >
      <RadioGroupItem id={id} value={valor} className="mt-0.5" />
      <span className="space-y-1">
        <span className="block text-sm font-semibold">{titulo}</span>
        <span className="text-muted-foreground block text-xs">{descripcion}</span>
      </span>
    </label>
  );
}

function Cargando() {
  return (
    <AuthLayout>
      <div className="flex flex-col items-center gap-3 py-12 text-center">
        <Spinner className="text-primary size-6" />
        <p className="text-muted-foreground text-caption">Abriendo tu cotización…</p>
      </div>
    </AuthLayout>
  );
}

/**
 * Pantalla de una sola pieza: ícono, título y explicación.
 *
 * NO lleva botón, al revés que su gemela de `ActivarCuenta`, y esa ausencia es
 * la decisión: allá el destino natural es "volver a iniciar sesión", y acá el
 * lector no tiene cuenta. Un enlace al login sería mandar a un cliente a una
 * puerta que no puede abrir. La salida es responder el correo, y eso lo dice
 * cada descripción.
 */
function Estado({
  icon,
  tono,
  titulo,
  descripcion,
}: {
  icon: React.ReactNode;
  tono: "error" | "neutro" | "exito";
  titulo: string;
  descripcion: string;
}) {
  const fondo = {
    error: "bg-destructive/10 text-destructive",
    neutro: "bg-muted text-muted-foreground",
    exito: "bg-accent text-accent-foreground",
  }[tono];

  return (
    <AuthLayout>
      <div className="mb-8">
        <div className={`mb-5 flex h-12 w-12 items-center justify-center rounded-2xl ${fondo}`}>
          {icon}
        </div>
        <h1 className="text-3xl font-extrabold tracking-tight">{titulo}</h1>
        <p className="text-muted-foreground text-caption mt-2">{descripcion}</p>
      </div>
    </AuthLayout>
  );
}
